/**
 * Author: Claude Code
 * Version: v3.2.62
 * Modified: 2025-10-10
 *
 * MapExporter - 完整地图PNG导出工具
 * 功能: 渲染完整的50x50地图(2400x2400px)并导出为PNG文件
 */

import React, { useEffect, useRef, useState } from 'react';
import { getGameManager } from '@game/GameManager';

const MapExporter: React.FC = () => {
  const [status, setStatus] = useState<string>('Initializing...');
  const [mapReady, setMapReady] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportedImage, setExportedImage] = useState<string | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let mounted = true;

    const initializeMap = async () => {
      try {
        setStatus('Starting Phaser3 game engine...');
        const gm = getGameManager();

        // 初始化游戏引擎（不连接WebSocket）
        await (gm as any).initialize?.({ connect: false });

        if (mounted) {
          gm.start();
          setStatus('Game engine started. Waiting for map rendering...');
        }
      } catch (error) {
        console.error('Failed to initialize game:', error);
        setStatus(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    };

    // 监听地图渲染完成事件
    const onMapReady = () => {
      if (mounted) {
        setMapReady(true);
        setStatus('✅ Map fully rendered! Ready to export.');
      }
    };

    window.addEventListener('ABR_MAP_READY', onMapReady);
    initializeMap();

    return () => {
      mounted = false;
      window.removeEventListener('ABR_MAP_READY', onMapReady);

      try {
        const gm = getGameManager();
        gm.destroy();
      } catch (error) {
        console.error('Error destroying game manager:', error);
      }
    };
  }, []);

  /**
   * 导出完整地图为PNG
   * 使用RenderTexture捕获完整2400x2400地图
   */
  const exportFullMap = () => {
    if (!mapReady || exporting) return;

    setExporting(true);
    setStatus('Exporting full map (2400x2400px)...');

    try {
      const gm = getGameManager();
      const gameContainer = (gm as any).gameContainer;

      if (!gameContainer) {
        throw new Error('GameContainer not found');
      }

      const game = gameContainer.getGame();

      if (!game || !game.scene) {
        throw new Error('Phaser game instance not found');
      }

      const battleScene = game.scene.scenes.find((s: any) => s.scene.key === 'BattleScene');

      if (!battleScene) {
        throw new Error('BattleScene not found');
      }

      // 地图配置
      const MAP_WIDTH = 50;  // 50 tiles
      const MAP_HEIGHT = 50; // 50 tiles
      const TILE_SIZE = 48;  // 48px per tile
      const FULL_MAP_WIDTH = MAP_WIDTH * TILE_SIZE;   // 2400px
      const FULL_MAP_HEIGHT = MAP_HEIGHT * TILE_SIZE; // 2400px

      setStatus(`Creating RenderTexture (${FULL_MAP_WIDTH}x${FULL_MAP_HEIGHT}px)...`);

      // 创建临时RenderTexture来捕获完整地图
      const renderTexture = battleScene.add.renderTexture(0, 0, FULL_MAP_WIDTH, FULL_MAP_HEIGHT);
      renderTexture.setOrigin(0, 0);

      // 保存当前camera状态
      const camera = battleScene.cameras.main;
      const originalZoom = camera.zoom;
      const originalScrollX = camera.scrollX;
      const originalScrollY = camera.scrollY;

      // 临时调整camera以覆盖整个地图
      camera.setZoom(1);
      camera.scrollX = 0;
      camera.scrollY = 0;
      camera.setBounds(0, 0, FULL_MAP_WIDTH, FULL_MAP_HEIGHT);

      setStatus('Rendering map layers...');

      // 获取所有地图相关的游戏对象
      const mapLayers = battleScene.children.list.filter((child: any) => {
        // 包括: tilemap layers, sprites (agents, resources), graphics (safezone)
        return child.type === 'TilemapLayer' ||
               child.type === 'Sprite' ||
               child.type === 'Image' ||
               (child.type === 'Graphics' && child.name?.includes('safezone'));
      });

      // 按Z-index排序，确保正确的渲染顺序
      mapLayers.sort((a: any, b: any) => (a.depth || 0) - (b.depth || 0));

      // 渲染所有图层到RenderTexture
      mapLayers.forEach((layer: any) => {
        if (layer.visible) {
          renderTexture.draw(layer, layer.x, layer.y);
        }
      });

      setStatus('Generating PNG image...');

      // 将RenderTexture转换为图片并下载
      renderTexture.snapshot((image: HTMLImageElement) => {
        try {
          // 创建canvas来导出图片
          const canvas = document.createElement('canvas');
          canvas.width = FULL_MAP_WIDTH;
          canvas.height = FULL_MAP_HEIGHT;
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            throw new Error('Failed to create canvas context');
          }

          ctx.drawImage(image, 0, 0);

          // 转换为DataURL
          const dataUrl = canvas.toDataURL('image/png');
          setExportedImage(dataUrl);

          // 自动下载
          const link = document.createElement('a');
          link.download = `battle-royale-map-${Date.now()}.png`;
          link.href = dataUrl;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          setStatus('✅ Map exported successfully!');
          setExporting(false);

          // 恢复camera状态
          camera.setZoom(originalZoom);
          camera.scrollX = originalScrollX;
          camera.scrollY = originalScrollY;

          // 清理RenderTexture
          renderTexture.destroy();

        } catch (error) {
          console.error('Failed to export image:', error);
          setStatus(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
          setExporting(false);
        }
      });

    } catch (error) {
      console.error('Export error:', error);
      setStatus(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setExporting(false);
    }
  };

  return (
    <div style={{
      padding: '20px',
      fontFamily: 'monospace',
      maxWidth: '1200px',
      margin: '0 auto'
    }}>
      <h1 style={{ marginBottom: '10px' }}>🗺️ Map Exporter</h1>
      <p style={{ color: '#666', marginBottom: '20px' }}>
        Export the complete 50x50 battle royale map as a PNG image (2400x2400px)
      </p>

      {/* Status Display */}
      <div style={{
        background: '#f5f5f5',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '20px',
        border: '1px solid #ddd'
      }}>
        <div style={{ marginBottom: '10px' }}>
          <strong>Status:</strong> {status}
        </div>
        <div style={{ marginBottom: '10px' }}>
          <strong>Map Ready:</strong> {mapReady ? '✅ Yes' : '⏳ Loading...'}
        </div>
        <div>
          <strong>Export Size:</strong> 2400 x 2400 pixels (50 tiles × 48px)
        </div>
      </div>

      {/* Export Button */}
      <button
        onClick={exportFullMap}
        disabled={!mapReady || exporting}
        style={{
          padding: '15px 30px',
          fontSize: '16px',
          fontWeight: 'bold',
          background: mapReady && !exporting ? '#4CAF50' : '#ccc',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: mapReady && !exporting ? 'pointer' : 'not-allowed',
          marginBottom: '20px',
          transition: 'all 0.3s'
        }}
      >
        {exporting ? '⏳ Exporting...' : mapReady ? '📥 Export Full Map as PNG' : '⏳ Waiting for map...'}
      </button>

      {/* Preview */}
      {exportedImage && (
        <div style={{ marginTop: '30px' }}>
          <h3>Preview (scaled down to 800px):</h3>
          <img
            src={exportedImage}
            alt="Exported map"
            style={{
              maxWidth: '800px',
              border: '2px solid #333',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
            }}
          />
          <p style={{ marginTop: '10px', color: '#666' }}>
            💡 The actual exported image is 2400x2400px. This is a scaled-down preview.
          </p>
        </div>
      )}

      {/* Phaser Game Container (Hidden) */}
      <div
        ref={canvasRef}
        id="phaser-game"
        style={{
          position: 'fixed',
          top: '-9999px',
          left: '-9999px',
          visibility: 'hidden',
          pointerEvents: 'none'
        }}
      />

      {/* Instructions */}
      <div style={{
        marginTop: '40px',
        padding: '20px',
        background: '#f9f9f9',
        borderRadius: '8px',
        border: '1px solid #ddd'
      }}>
        <h3>📖 Instructions</h3>
        <ol style={{ lineHeight: '1.8' }}>
          <li>Wait for the map to fully render (status shows "Map Ready")</li>
          <li>Click "Export Full Map as PNG" button</li>
          <li>The complete 2400x2400px map will be automatically downloaded</li>
          <li>The exported image includes:
            <ul>
              <li>✅ All terrain tiles (plains, water, mountains, forests, buildings)</li>
              <li>✅ Safe zone visualization (if active)</li>
              <li>✅ Agent sprites (if spawned)</li>
              <li>✅ Resource items (if spawned)</li>
            </ul>
          </li>
        </ol>
      </div>
    </div>
  );
};

export default MapExporter;
